import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BookOpen, Calendar } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo, { JsonLd, schemas } from "../components/Seo";
import { api } from "../lib/api";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Blog() {
  const { data, isLoading } = useQuery({
    queryKey: ["articles"],
    queryFn: () => api.get("/articles").then((r) => r.data.items),
  });
  const articles = data || [];

  return (
    <div className="min-h-screen bg-[var(--bdd-bg)]" data-testid="page-blog">
      <Seo
        title="Guide e News sui set di costruzioni"
        description="Articoli, guide all'acquisto, recensioni e curiosità sui set di costruzioni LEGO. Aggiornati settimanalmente per appassionati italiani."
        path="/guide"
      />
      <JsonLd
        data={schemas.breadcrumb([
          { name: "Home", url: "/" },
          { name: "Guide e News", url: "/guide" },
        ])}
      />
      <Header />

      <section className="border-b border-zinc-900 bg-gradient-to-b from-[#0d162d]/60 to-transparent">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="font-mono-bdd text-xs uppercase tracking-[0.3em] text-[#FBD51C] mb-3">
            Blog
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-4">
            Guide e News
          </h1>
          <p className="text-zinc-400 text-lg max-w-3xl">
            Articoli, guide all&apos;acquisto, recensioni e curiosità sul mondo dei
            mattoncini. Selezionati e aggiornati ogni settimana.
          </p>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {isLoading && (
          <div className="grid gap-5">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-40 rounded-2xl shimmer" />
            ))}
          </div>
        )}

        {!isLoading && articles.length === 0 && (
          <div className="text-center py-20" data-testid="blog-empty">
            <BookOpen size={32} className="mx-auto text-zinc-600 mb-3" />
            <div className="text-zinc-400 text-lg mb-2">
              Stiamo preparando i primi articoli.
            </div>
            <div className="text-sm text-zinc-500">
              Torna a trovarci tra qualche giorno!
            </div>
          </div>
        )}

        {!isLoading && articles.length > 0 && (
          <div className="grid gap-5">
            {articles.map((a) => (
              <article
                key={a.id}
                data-testid={`article-card-${a.slug}`}
                className="bg-zinc-900 border border-zinc-800 hover:border-[#FBD51C]/40 rounded-2xl overflow-hidden transition-colors"
              >
                <Link to={`/guide/${a.slug}`} className="grid md:grid-cols-3 gap-0">
                  {a.cover_image && (
                    <div className="md:col-span-1 aspect-video md:aspect-auto bg-zinc-950">
                      <img
                        src={a.cover_image}
                        alt={a.title}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className={`${a.cover_image ? "md:col-span-2" : "md:col-span-3"} p-6`}>
                    <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                      <Calendar size={12} />
                      {formatDate(a.created_at)}
                    </div>
                    <h2 className="font-display text-xl sm:text-2xl text-white mb-2 line-clamp-2">
                      {a.title}
                    </h2>
                    {a.excerpt && (
                      <p className="text-zinc-400 text-sm leading-relaxed line-clamp-3 mb-3">
                        {a.excerpt}
                      </p>
                    )}
                    <span className="inline-flex items-center gap-1 text-sm text-[#FBD51C] font-bold">
                      Leggi l&apos;articolo <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
